import { getSupabaseClient } from '../../lib/supabase'
import { normalizeAppLocale, traceAppLocale, type AppLocale } from '../../../supabase/functions/_shared/app-locale'

export type HomeDiagnosis = {
  summary: string
  likely_causes: string[]
  confidence: 'low' | 'medium' | 'high'
  risk_level: 'low' | 'moderate' | 'high' | 'urgent'
  emergency: boolean
  immediate_actions: string[]
  recommended_trade: string
  estimated_cost_min: number
  estimated_cost_max: number
  estimated_time: string
  questions_for_provider: string[]
  disclaimer: string
}

export type SavedHomeDiagnosis = {
  id: string
  problem_description: string
  diagnosis: HomeDiagnosis
  generation_locale: AppLocale
  created_at: string
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

function isHomeDiagnosis(value: unknown): value is HomeDiagnosis {
  if (!value || typeof value !== 'object') return false
  const item = value as Record<string, unknown>
  return typeof item.summary === 'string'
    && isStringArray(item.likely_causes)
    && ['low', 'medium', 'high'].includes(String(item.confidence))
    && ['low', 'moderate', 'high', 'urgent'].includes(String(item.risk_level))
    && typeof item.emergency === 'boolean'
    && isStringArray(item.immediate_actions)
    && typeof item.recommended_trade === 'string'
    && typeof item.estimated_cost_min === 'number'
    && typeof item.estimated_cost_max === 'number'
    && typeof item.estimated_time === 'string'
    && isStringArray(item.questions_for_provider)
    && typeof item.disclaimer === 'string'
}

type HomeDiagnosisInput = {
  organizationId: string
  propertyId: string
  categoryId: string
  problem: string
  categoryName: string
  propertySummary: string
  locale: AppLocale
}

export function buildHomeDiagnosisInvocation(input: HomeDiagnosisInput) {
  return { ...input, locale: normalizeAppLocale(input.locale) }
}

export async function runHomeDiagnosis(input: HomeDiagnosisInput): Promise<HomeDiagnosis> {
  const locale = normalizeAppLocale(input.locale)
  traceAppLocale('client.home-diagnosis', input.locale, locale, import.meta.env.DEV)
  const invocation = await getSupabaseClient().functions.invoke('amigo-home-diagnosis', { body: buildHomeDiagnosisInvocation(input) })
  if (invocation.error) {
    const technicalMessage = invocation.error instanceof Error ? invocation.error.message : 'function_error'
    if (import.meta.env.DEV) console.error('AMIGO Home Diagnosis request failed.', { technicalMessage })
    throw new Error(locale === 'es' ? 'No pudimos completar el diagnóstico en este momento. Inténtalo nuevamente.' : 'We couldn’t complete the diagnosis right now. Please try again.')
  }
  const response: unknown = invocation.data
  if (!response || typeof response !== 'object') throw new Error('AMIGO did not return a diagnosis.')
  const payload = response as Record<string, unknown>
  if (!isHomeDiagnosis(payload.diagnosis) || payload.generationLocale !== locale) {
    if (import.meta.env.DEV) console.error('AMIGO Home Diagnosis returned an invalid contract.', { code: payload.error ?? 'invalid_response', generationLocale: payload.generationLocale })
    throw new Error(locale === 'es' ? 'No pudimos completar el diagnóstico en este momento. Inténtalo nuevamente.' : 'We couldn’t complete the diagnosis right now. Please try again.')
  }
  return payload.diagnosis
}

export async function getLatestHomeDiagnosis(propertyId: string, categoryId: string): Promise<SavedHomeDiagnosis | null> {
  const { data, error } = await getSupabaseClient()
    .from('amigo_home_diagnoses' as never)
    .select('id,problem_description,diagnosis,generation_locale,created_at')
    .eq('property_id', propertyId)
    .eq('category_id', categoryId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle() as { data: SavedHomeDiagnosis | null; error: Error | null }
  if (error) throw error
  return data
}

export function buildDiagnosisRequestDescription(problem: string, diagnosis: HomeDiagnosis) {
  return [
    `Customer-reported problem:\n${problem.trim()}`,
    `AMIGO Home Diagnosis™ preliminary summary:\n${diagnosis.summary}`,
    `Possible causes (not confirmed):\n${diagnosis.likely_causes.map((item) => `• ${item}`).join('\n')}`,
    `Recommended immediate actions:\n${diagnosis.immediate_actions.map((item) => `• ${item}`).join('\n')}`,
    `Questions for the provider:\n${diagnosis.questions_for_provider.map((item) => `• ${item}`).join('\n')}`,
    `Planning range: $${Math.round(diagnosis.estimated_cost_min).toLocaleString()}–$${Math.round(diagnosis.estimated_cost_max).toLocaleString()} | Estimated time: ${diagnosis.estimated_time}`,
    `Risk: ${diagnosis.risk_level} | AI confidence: ${diagnosis.confidence}`,
    diagnosis.disclaimer,
  ].join('\n\n')
}

export async function publishRequestFromDiagnosis(input: {
  organizationId: string
  propertyId: string
  categoryId: string
  userId: string
  title: string
  description: string
  city: string
  region: string
  postalCode: string
  budgetMin: number
  budgetMax: number
}): Promise<string> {
  const { data, error } = await getSupabaseClient()
    .from('service_requests')
    .insert({
      organization_id: input.organizationId,
      property_id: input.propertyId,
      category_id: input.categoryId,
      title: input.title.trim(),
      description: input.description.trim(),
      city: input.city.trim(),
      region: input.region.trim(),
      postal_code: input.postalCode.trim(),
      budget_min_cents: Math.max(0, Math.round(input.budgetMin * 100)),
      budget_max_cents: Math.max(0, Math.round(input.budgetMax * 100)),
      status: 'published',
      created_by: input.userId,
    })
    .select('id')
    .single()
  if (error || !data) throw error ?? new Error('Unable to publish the service request.')
  return data.id
}
